import { useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Plus, Users, ChevronRight, LogOut, ImagePlus } from 'lucide-react';
import { useAuth } from '@/features/auth/context/AuthContext';
import { createWorkspaceApi } from '@/features/auth/api/authApi';
import { readImageAsDataUrl } from '@/features/shared/imageUpload';
import { Logo } from '@/shared/brand';

const COLORS = ['#C9A24A', '#0F172A', '#0E7490', '#9333EA', '#DC2626', '#059669'];

export default function WorkspaceSelectPage() {
  const { user, workspaces, setActiveWorkspace, refreshWorkspaces, logout } = useAuth();
  const navigate = useNavigate();

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [primaryColor, setPrimaryColor] = useState(COLORS[0]);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleSelect = (workspaceId: string) => {
    setActiveWorkspace(workspaceId);
    navigate('/vault');
  };

  const slugify = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const pickLogo = async (file: File | undefined) => {
    if (!file) return;
    try {
      setLogo(await readImageAsDataUrl(file, 256));
    } catch {
      setError('Could not read that image');
    }
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setCreating(true);
    setError(null);

    try {
      const ws = await createWorkspaceApi({
        name: name.trim(),
        slug: slugify(name),
        logo,
        primaryColor,
      });
      await refreshWorkspaces();
      setActiveWorkspace(ws.workspace?._id || ws._id || ws.id);
      navigate('/vault');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  const initials = (s: string) =>
    s.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen bg-canvas flex flex-col text-ink-900">
      {/* Top bar */}
      <header className="nav-marble border-b border-ink-100 safe-pt">
        <div className="h-14 flex items-center justify-between px-5 sm:px-8">
          <Logo size="sm" showTagline tagline="Workspace" />
          <button
            onClick={logout}
            className="flex items-center gap-2 text-ink-400 hover:text-ink-800 text-[13px] font-medium transition-colors cursor-pointer focus-ring rounded-lg px-3 py-1.5 hover:bg-ink-50"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-3 md:px-6 py-8 md:py-12">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[680px]"
        >
          {/* Card container */}
          <div className="bg-white rounded-[28px] p-6 sm:p-10 shadow-card border border-ink-100/70">
            {/* Header */}
            <div className="text-center mb-8">
              <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-400 mb-2.5">
                {user?.name ? `Welcome back, ${user.name}` : 'Welcome'}
              </p>
              <h1 className="font-headline text-[28px] sm:text-[32px] leading-tight text-ink-900">
                {workspaces.length > 0 ? 'Select a workspace' : 'Create your first workspace'}
              </h1>
              <p className="mt-2 text-[14px] text-ink-500 leading-relaxed">
                {workspaces.length > 0
                  ? 'Choose a workspace to continue, or create a new one.'
                  : 'Get started by setting up a workspace for your team.'}
              </p>
            </div>

            {error && (
              <div className="mb-5 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                {error}
              </div>
            )}

            {/* Workspace grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {workspaces.map((ws, i) => (
                <motion.button
                  key={ws.workspaceId}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => handleSelect(ws.workspaceId)}
                  className="group relative flex items-center gap-3.5 rounded-xl border border-ink-100 bg-canvas/50 p-4 text-left transition-all hover:border-gold-300 hover:bg-gold-50/30 hover:shadow-sm cursor-pointer focus-ring"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-gold-100 to-gold-50 text-gold-700 font-bold text-[13px] border border-gold-200/60">
                    {ws.workspaceLogo ? (
                      <img src={ws.workspaceLogo} alt="" className="h-full w-full rounded-xl object-cover" />
                    ) : (
                      initials(ws.workspaceName)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-ink-900 truncate">{ws.workspaceName}</p>
                    <span className="inline-flex items-center mt-1 rounded-md bg-ink-50 border border-ink-100 px-1.5 py-0.5 text-[10.5px] font-medium text-ink-500 capitalize">
                      {ws.workspaceRole}
                    </span>
                  </div>
                  <ChevronRight
                    size={15}
                    className="text-ink-300 group-hover:text-gold-500 transition-colors shrink-0"
                  />
                </motion.button>
              ))}

              {!showCreate && (
                <motion.button
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: workspaces.length * 0.05 }}
                  onClick={() => setShowCreate(true)}
                  className="group flex items-center justify-center gap-2.5 rounded-xl border-2 border-dashed border-ink-200 p-4 text-ink-400 transition-all hover:border-gold-300 hover:text-gold-600 hover:bg-gold-50/20 cursor-pointer focus-ring"
                >
                  <Plus size={16} />
                  <span className="text-[13px] font-semibold">New workspace</span>
                </motion.button>
              )}
            </div>

            {/* Create workspace form */}
            {showCreate && (
              <motion.form
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={handleCreate}
                className="mt-5 rounded-xl border border-ink-100 bg-canvas/60 p-5"
              >
                <h3 className="text-[13px] font-semibold text-ink-900 mb-4 flex items-center gap-2">
                  <Users size={15} className="text-gold-500" />
                  New workspace
                </h3>

                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="relative flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-ink-200 bg-white text-ink-400 hover:border-gold-300"
                      title="Upload logo"
                    >
                      {logo ? <img src={logo} alt="" className="h-full w-full object-cover" /> : <ImagePlus size={20} />}
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickLogo(e.target.files?.[0])} />
                    <div className="flex-1">
                      <label className="block text-[12px] font-semibold text-ink-500 mb-1.5 uppercase tracking-wide">
                        Brand name
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Vela Atelier"
                        required
                        autoFocus
                        className="w-full rounded-xl border border-ink-200 bg-white px-4 py-2.5 text-[14px] text-ink-900 placeholder:text-ink-300 focus:outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-100 transition-colors"
                      />
                      {name && (
                        <p className="mt-1.5 text-[11px] text-ink-400">
                          Slug: <span className="text-ink-600 font-medium">{slugify(name)}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold text-ink-500 mb-1.5 uppercase tracking-wide">Brand color</label>
                    <div className="flex items-center gap-2">
                      {COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setPrimaryColor(c)}
                          className={`h-7 w-7 rounded-full border-2 transition-transform ${primaryColor === c ? 'scale-110 border-ink-900' : 'border-transparent'}`}
                          style={{ background: c }}
                          aria-label={c}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    <button
                      type="submit"
                      disabled={creating || !name.trim()}
                      className="btn-press btn-shine rounded-xl bg-ink-900 hover:bg-ink-800 text-white font-semibold text-[13px] px-5 py-2.5 transition-colors disabled:opacity-50 cursor-pointer focus-ring"
                    >
                      {creating ? 'Creating…' : 'Create workspace'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreate(false);
                        setName('');
                        setError(null);
                      }}
                      className="text-[13px] font-medium text-ink-400 hover:text-ink-700 transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.form>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
